import React from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import Layout from '../components/Layout';
import CallToAction from '../components/CallToAction';
import PostHeader from '../components/PostHeader';
import PostNav from '../components/PostNav';
import { PostTags } from '../components/PostCard';
import Prose from '../components/Prose';
import Button from '../components/ui/Button';
import Container from '../components/ui/Container';
import Section from '../components/ui/Section';
import usePageMeta from '../hooks/usePageMeta';
import { BLOG_IMAGE, BLOG_IMAGE_ALT, getPost, type BlogPost as Post } from '../data/blog';
import NotFound from './NotFound';

const Tags = styled(PostTags)`
  margin-top: 2.5rem;
`;

const Footer = styled.div`
  margin-top: clamp(2.5rem, 5vw, 3.5rem);
  padding-top: 1.5rem;
  border-top: 1px solid var(--border);
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  align-items: stretch;

  > a {
    align-self: flex-start;
  }
`;

const PostView: React.FC<{ post: Post }> = ({ post }) => {
  usePageMeta({
    title: post.seoTitle || post.title,
    description: post.description,
    path: `/blog/${post.slug}`,
    type: 'article',
    image: post.image || BLOG_IMAGE,
    imageAlt: post.image ? post.coverAlt ?? undefined : BLOG_IMAGE_ALT,
    publishedTime: post.date,
    modifiedTime: post.updated ?? post.date,
    tags: post.tags,
    lang: post.lang,
    noindex: post.draft,
    jsonLd: post.jsonLd,
  });

  return (
    <Layout>
      <article aria-labelledby="post-title">
        <PostHeader post={post} />
        <Section $tone="base" $flushTop>
          <Container $narrow>
            <Prose html={post.html} />
            {post.tags.length > 0 && (
              <Tags aria-label="Post tags">
                {post.tags.map((tag) => (
                  <li key={tag}>{tag}</li>
                ))}
              </Tags>
            )}
            <Footer>
              <PostNav slug={post.slug} lang={post.lang} />
              <Button to="/blog" variant="secondary">
                {post.lang === 'es' ? 'Volver al blog' : 'Back to blog'}
              </Button>
            </Footer>
          </Container>
        </Section>
      </article>
      <CallToAction lang={post.lang === 'es' ? 'es' : 'en'} />
    </Layout>
  );
};

const BlogPost: React.FC = () => {
  const { slug } = useParams();
  const post = getPost(slug);
  if (!post) return <NotFound />;
  return <PostView post={post} />;
};

export default BlogPost;
